import { useCallback, useSyncExternalStore } from 'react'
import { useAppSelector } from 'hooks'

/** Per-account storage key — a shared browser must never dim account B's board with account A's history. */
const storageKeyFor = (userId: string | undefined): string => `boxTruckLoads:viewed:${userId ?? 'anon'}`

/** Viewed marks older than this are pruned — board loads expire within hours, so week-old keys are dead
 *  weight that would only grow the entry list. */
const TTL_MS = 7 * 24 * 60 * 60 * 1000
/** Hard cap on stored entries (newest kept) so a heavy triage week can't grow the key unbounded. */
const MAX_ENTRIES = 2000

type StoredEntry = [key: string, viewedAt: number]

const readEntries = (storageKey: string): StoredEntry[] => {
    try {
        const raw = localStorage.getItem(storageKey)
        if (!raw) return []
        const parsed = JSON.parse(raw) as unknown
        if (!Array.isArray(parsed)) return []
        const cutoff = Date.now() - TTL_MS
        return (parsed as StoredEntry[]).filter((e) => Array.isArray(e) && typeof e[0] === 'string' && typeof e[1] === 'number' && e[1] > cutoff)
    } catch {
        return [] // corrupt/blocked storage — start clean, dimming is a convenience
    }
}

const writeEntries = (storageKey: string, entries: StoredEntry[]): void => {
    try {
        localStorage.setItem(storageKey, JSON.stringify(entries.slice(-MAX_ENTRIES)))
    } catch {
        // Quota/private mode — the in-memory set still works for this session.
    }
}

/**
 * ONE set of marks per account, shared by every component that mounts this hook.
 *
 * The table dims the rows, but "Mark all read" sits in the toolbar above it — two components, and with a
 * `useState` copy each the button would have written marks the table never re-rendered for. An external
 * store keyed by the storage key means both read the same value and both update on any write; the lazy read
 * also replaces the re-hydrate effect, since a different account is simply a different key.
 */
const caches = new Map<string, Set<string>>()
const listeners = new Set<() => void>()

const snapshot = (storageKey: string): Set<string> => {
    const cached = caches.get(storageKey)
    if (cached) return cached
    const hydrated = new Set(readEntries(storageKey).map((e) => e[0]))
    caches.set(storageKey, hydrated)
    return hydrated
}

/** Publish a NEW set (never mutate the live one) — useSyncExternalStore compares snapshots by identity. */
const publish = (storageKey: string, next: Set<string>): void => {
    caches.set(storageKey, next)
    listeners.forEach((notify) => notify())
}

const subscribe = (notify: () => void): (() => void) => {
    listeners.add(notify)
    return () => {
        listeners.delete(notify)
    }
}

/** Persist the marks, read-modify-write, so parallel tabs lose at most their own last write. */
const persist = (storageKey: string, keys: string[]): void => {
    const written = new Set(keys)
    const now = Date.now()
    const entries = readEntries(storageKey).filter((e) => !written.has(e[0]))
    keys.forEach((key) => entries.push([key, now]))
    writeEntries(storageKey, entries)
}

interface ViewedBoxTruckLoads {
    viewedKeys: Set<string>
    markViewed: (key: string) => void
    /** Mark a whole batch at once — the board's "Mark all read". Keys already marked are skipped, so a
     *  second click is a no-op rather than a re-timestamp of the entire board. */
    markAllViewed: (keys: string[]) => void
}

/**
 * The operator's "already opened" load marks for the box-truck boards — PERSISTED per account in
 * localStorage (7-day TTL, capped), so the dimmed treatment survives feed refreshes, table remounts,
 * and full page reloads instead of resetting every render pass like the old in-memory set did.
 */
export const useViewedBoxTruckLoads = (): ViewedBoxTruckLoads => {
    const userId = useAppSelector((state) => state.auth.user?.id)
    const storageKey = storageKeyFor(userId)
    const viewedKeys = useSyncExternalStore(
        subscribe,
        useCallback(() => snapshot(storageKey), [storageKey])
    )

    const markMany = useCallback(
        (keys: string[]) => {
            const current = snapshot(storageKey)
            const fresh = keys.filter((key) => !current.has(key))
            if (fresh.length === 0) return
            const next = new Set(current)
            fresh.forEach((key) => next.add(key))
            publish(storageKey, next)
            persist(storageKey, fresh)
        },
        [storageKey]
    )

    const markViewed = useCallback((key: string) => markMany([key]), [markMany])

    return { viewedKeys, markViewed, markAllViewed: markMany }
}
